import React, {
  ChangeEvent, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import Input from './atoms/Input';
import Button from './atoms/Button';
import Accordion from './atoms/Accordion';
import { RootState, useCheckCouponValidationMutation } from '../store';
import CouponDTO from '../types/Coupon';
import useQuery from '../hooks/useQuery';
import { toPercentage } from '../utils/shared';

interface GotCouponProps {
  onCouponChange: (coupon: CouponDTO | null) => void;
  totalToPay?: number;
  hasOpenPrice?: boolean;
  isBackoffice?: boolean;
  expanded?: boolean;
}

const GotCoupon: React.FC<GotCouponProps> = ({
  onCouponChange, totalToPay, hasOpenPrice = false, isBackoffice = false, expanded = false,
}) => {
  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const orderItemData = useSelector(
    (state: RootState) => state.orders.orderItemData,
  );
  const query = useQuery();

  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const [code, setCode] = useState('');
  const [checkCouponValidation, couponValidationResults] = useCheckCouponValidationMutation();

  // A coupon persisted from a previous step or passed through the ?coupon= link.
  // Backoffice sellers must apply coupons deliberately, never auto-applied.
  const loadedCode = useMemo(() => {
    if (!event?.id || isBackoffice) {
      return null;
    }
    try {
      const storedValue = JSON.parse(
        localStorage.getItem(`${event.id}_coupon`) || '{}',
      );
      return storedValue?.coupon?.code ?? query.get('coupon') ?? null;
    } catch (error) {
      console.warn('Failed to parse coupon from localStorage:', error);
      localStorage.removeItem(`${event.id}_coupon`);
      return query.get('coupon') ?? null;
    }
    // query is a fresh URLSearchParams each render, so key off the event only
  }, [event?.id, isBackoffice]);

  const validateCoupon = (codeVal: string) => {
    checkCouponValidation({
      businessId: event?.business_id || '',
      eventId: event?.id || '',
      amount: orderItemData.length,
      couponCode: codeVal,
    });
  };

  // Persist the coupon so it survives navigation, then notify the parent.
  const emitCouponChange = (coupon: CouponDTO | null) => {
    if (event?.id) {
      localStorage.setItem(`${event.id}_coupon`, JSON.stringify({ coupon }));
    }
    onCouponChange(coupon);
  };

  useEffect(() => {
    if (loadedCode) {
      setCode(loadedCode);
      validateCoupon(loadedCode);
    }
  }, [loadedCode]);

  const handleApplyDiscountClick = () => {
    if (isCouponApplied) {
      setIsCouponApplied(false);
      setSuccessMsg('');
      emitCouponChange(null);
    } else {
      validateCoupon(code);
    }
  };

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<unknown>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const result = couponValidationResults.data?.data;
    if (result && result.valid && result.coupon) {
      emitCouponChange(result.coupon);
      setIsCouponApplied(true);
      setSuccessMsg(`¡Se aplicó un cupón del ${toPercentage(result.coupon.value)}%!`);
    }
  }, [couponValidationResults]);

  const getErrMsg = () => {
    if (couponValidationResults?.data?.data) {
      if (!couponValidationResults.data.data.valid) {
        const { reason } = couponValidationResults.data.data;
        const { coupon } = couponValidationResults.data.data;
        if (reason === 'sold out') {
          if (coupon) {
            const remainingAmount = coupon.limit_amount - coupon.taken_amount;
            if (remainingAmount > 0) {
              return `Este descuento puede aplicarse solo a ${remainingAmount} ${remainingAmount === 1 ? 'entrada' : 'entradas'}`;
            }
            return 'Cupón agotado';
          }
        }
        return 'Cupón no es válido';
      }
    }
    return '';
  };

  // Backoffice sellers always get the coupon field: they may need to apply a
  // coupon the event was not flagged as having. On the public side it is only
  // relevant when the event has coupons and there is a positive amount to
  // discount (or a coupon is already applied); open-price tickets are excluded.
  const shouldShow = isBackoffice
    || (!!event?.has_coupons
      && ((!!totalToPay && totalToPay > 0 && !hasOpenPrice) || isCouponApplied));

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="w-full rounded-3xl">
      <Accordion title="Cupón de descuento" isExpanded={loadedCode !== null || expanded} padding='p-3' titleClassName='text-text-primary text-lg font-bold'>
        <div>
          <div className="flex flex-col md:flex-row justify-center items-start gap-[20px] self-stretch flex-1">
            <div className="w-full md:w-3/4">
              <Input
                id="code"
                label="Ingresá acá el cupón"
                value={code}
                onKeyDown={handleKeyDown}
                onChange={handleCodeChange}
                errMsg={getErrMsg()}
                disabled={isCouponApplied}
                additionalClasses='h-12 w-full'
              />
            </div>
            <div className="w-full md:w-1/4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleApplyDiscountClick}
                onKeyDown={handleKeyDown}
                loading={couponValidationResults.isLoading}
                disabled={!code}
                className="w-full"
              >
                {isCouponApplied ? 'Cancelar' : 'Aplicar'}
              </Button>
            </div>
          </div>
          <span className="flex items-center justify-center text-green-500 white-text-shadow">{successMsg}</span>
        </div>
      </Accordion>
    </div>
  );
};

export default GotCoupon;
